<template>
  <section
    id="rvCarousel"
    ref="RvCarousel"
    :aria-label="arialabel"
    aria-roledescription="carousel"
    class="no-touch"
    :class="[`rv-carousel--${sizing}`]"
    @mouseover="handleRotation(false)"
    @mouseleave="handleRotation(rotating ? true : false)"
  >
    <div :class="[`rv-carousel__inner--${sizing}`]">
      <button
        v-if="allowRotation"
        :aria-label="rotating ? stop : start"
        aria-controls="rvCarousel-items"
        class="rv-carousel__btn rv-carousel__btn--pause bg-primary hover:bg-accent-9 focus:bg-accent-9 focus-b"
        @click=";(rotate = !rotate), (rotating = !rotating)"
        @focus="handleRotation(false)"
        @blur="handleRotation(rotating ? true : false)"
      >
        <IconBase
          :icon="rotating ? 'PauseIcon' : 'BackIcon'"
          :color="`white`"
          :size="`1.5rem`"
          class="rotate-180"
        />
      </button>

      <!-- Left right buttons -->
      <ul v-if="buttons">
        <li>
          <button
            aria-label="Previous Slide"
            aria-controls="rvCarousel-items"
            class="rv-carousel__btn rv-carousel__btn--previous md:bg-primary pr-1 py-2 focus-b"
            @click="handlePrevNext('prev')"
            @focus="handleRotation(false)"
            @blur="handleRotation(rotating ? true : false)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="32" viewBox="0 0 17 32" v-bind:svg-inline="''" v-bind:class="'h-full w-full'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#fff" d="M16.614 31.748l.509-.509a.86.86 0 000-1.22L3.1 16.004 17.123 1.982a.86.86 0 000-1.22l-.509-.509a.86.86 0 00-1.22 0L.252 15.387a.86.86 0 000 1.22l15.142 15.134a.853.853 0 001.219.007z"/></svg>
          </button>
        </li>
        <li>
          <button
            aria-label="Next Slide"
            aria-controls="rvCarousel-items"
            class="rv-carousel__btn rv-carousel__btn--next md:bg-primary pl-1 py-2 focus-b"
            @click="handlePrevNext('next')"
            @focus="handleRotation(false)"
            @blur="handleRotation(rotating ? true : false)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="32" viewBox="0 0 17 32" v-bind:svg-inline="''" v-bind:class="'h-full w-full rotate-180'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#fff" d="M16.614 31.748l.509-.509a.86.86 0 000-1.22L3.1 16.004 17.123 1.982a.86.86 0 000-1.22l-.509-.509a.86.86 0 00-1.22 0L.252 15.387a.86.86 0 000 1.22l15.142 15.134a.853.853 0 001.219.007z"/></svg>
          </button>
        </li>
      </ul>

      <ul v-if="!buttons" class="rv-carousel__btn-list">
        <li v-for="(image, i) in gallery" :key="`${image.id}-${image.id}`">
          <button
            :aria-label="image.title"
            class="p-3 rounded-full focus-a group"
            @click=";(currentIndex = i), handleLazyLoad()"
          >
            <span
              class="block h-2 w-2 rounded-full border border-white group-hover:bg-secondary group-focus:bg-secondary"
              :class="[currentIndex === i ? 'bg-primary' : 'bg-tertiary']"
            />
          </button>
        </li>
      </ul>

      <div
        id="rvCarousel-items"
        ref="RvCarouselItems"
        :aria-live="rotate ? 'off' : 'polite'"
        class="rv-carousel__items"
      >
        <TransitionGroup name="rv-carousel" mode="in-out">
          <div
            v-for="(image, i) in gallery"
            v-show="currentIndex === i"
            :key="`${image.id}`"
            role="group"
            aria-roledescription="slide"
            class="rv-carousel__item"
          >
            <img
              :src="lazyLoad.includes(i) ? image.sizes.large : ''"
              :alt="`Gallery item ${i + 1} of ${gallery.length}, ${image.alt}`"
              :class="[`rv-carousel__image--${sizing}`]"
            />
            <div
              v-if="titles"
              class="rv-carousel__info bg-primary-95 text-white font-bold"
            >
              <h3 class="rv-carousel__title">
                <span class="rv-carousel__count" aria-hidden="true">
                  {{ currentIndex + 1 }}/{{ gallery.length }}
                </span>
                &nbsp; {{ image.title }}
              </h3>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
  </section>
</template>

<script>
import IconBase from '@/components/icons/IconBase.vue'

export default {
  name: 'RvCarousel',

  components: {
    IconBase
  },

  props: {
    arialabel: {
      type: String,
      default: 'Gallery'
    },
    post: {
      type: Object,
      default: () => {}
    },
    titles: {
      type: Boolean,
      default: true
    },
    buttons: {
      type: Boolean,
      default: true
    },
    sizing: {
      type: String,
      default: 'scale'
    },
    allowRotation: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    currentIndex: 0,
    rotate: false,
    rotating: false,
    interval: null,
    stop: 'Stop automatic slide show',
    start: 'Start automatic slideshow',
    touchstartX: 0,
    touchendX: 0,
    isTouch: false,
    lazyLoad: [0]
  }),

  computed: {
    swipeZone() {
      return this.$refs.RvCarouselItems
    },

    gallery() {
      return this.post.gallery
    }
  },

  watch: {
    rotate: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.interval = setInterval(() => {
            this.currentIndex =
              this.currentIndex + 1 < this.gallery.length
                ? this.currentIndex + 1
                : 0
            this.handleLazyLoad()
          }, 3000)
        } else {
          clearInterval(this.interval)
        }
      }
    }
  },

  mounted() {
    this.addListeners()

    if (this.allowRotation) {
      this.rotate = true
      this.rotating = true
    }
  },

  beforeDestroy() {
    window.removeEventListener('keyup', this.handleArrowKeys)
    window.removeEventListener('touchstart', this.handleTouch)
  },

  methods: {
    handlePrevNext(direction) {
      const length = this.gallery.length
      const index = this.currentIndex

      if (this.isTouch) {
        this.rotate = false
        this.rotating = false
      }

      switch (direction) {
        case 'next':
          this.currentIndex = (index + 1) % length
          break
        case 'prev':
          this.currentIndex = (index === 0 && length - 1) || index - 1
          break
        default:
          this.currentIndex = index
      }

      this.handleLazyLoad()
    },
    handleArrowKeys() {
      switch (event.keyCode) {
        case 39:
          this.handlePrevNext('next')
          break
        case 37:
          this.handlePrevNext('prev')
          break
        default:
      }
    },
    handleRotation(value) {
      if (!this.isTouch) {
        this.rotate = value
      }
    },
    addListeners() {
      window.addEventListener('keyup', this.handleArrowKeys)
      window.addEventListener('touchstart', this.handleTouch)

      this.swipeZone.addEventListener(
        'touchstart',
        event => {
          this.touchstartX = event.changedTouches[0].screenX
        },
        false
      )

      this.swipeZone.addEventListener(
        'touchend',
        event => {
          this.touchendX = event.changedTouches[0].screenX
          this.handleSwipe()
        },
        false
      )
    },
    handleTouch() {
      window.removeEventListener('touchstart', this.handleTouch)
      this.$refs.RvCarousel.classList.remove('no-touch')
      this.isTouch = true
    },
    handleSwipe() {
      if (this.isTouch) {
        this.rotate = false
        this.rotating = false
      }

      if (this.touchendX < this.touchstartX) {
        this.handlePrevNext('prev')
      }

      if (this.touchendX > this.touchstartX) {
        this.handlePrevNext('next')
      }
    },
    handleLazyLoad() {
      if (!this.lazyLoad.includes(this.currentIndex)) {
        this.lazyLoad.push(this.currentIndex)
      }
    }
  }
}
</script>

<style>
.rv-carousel--scale {
  /* background-image: url('https://www.transparenttextures.com/patterns/45-degree-fabric-dark.png'); */
  height: 0;
  /* padding-top: 56.25%; */
  padding-top: 75%;
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 1;
}
.rv-carousel--fit {
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 1;
}
.rv-carousel__inner--scale {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}
.rv-carousel__inner--fit {
  height: 100%;
  width: 100%;
}
.rv-carousel__btn {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 3rem;
  position: absolute;
  transition: all 0.3s;
  width: 3rem;
  z-index: 10;
}
.rv-carousel__btn-list {
  display: flex;
  position: absolute;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}
.rv-carousel__btn svg {
}
.no-touch .rv-carousel__btn:hover,
.no-touch .rv-carousel__btn:focus {
}
.no-touch .rv-carousel__btn:hover svg,
.no-touch .rv-carousel__btn:focus svg {
}
.rv-carousel__btn--pause {
  top: 0;
}
.rv-carousel__btn--previous {
  left: 0;
  top: 45%;
  transform: translateY(-45%);
}
.rv-carousel__btn--next {
  right: 0;
  top: 45%;
  transform: translateY(-45%);
}
.rv-carousel__items {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.rv-carousel__item {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}
.rv-carousel__image--scale {
  height: 100%;
  margin: auto;
  width: auto;
}
.rv-carousel__image--fit {
  height: 100%;
  width: 100%;
  object-fit: cover;
}
.rv-carousel__info {
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: 0;
  min-height: 3rem;
  padding: 0.5rem;
  position: absolute;
  text-align: center;
  width: 100%;
}
.rv-carousel__title {
}
.rv-carousel__count {
}

/* Transitions */
.rv-carousel-enter-active,
.rv-carousel-leave-active {
  transition: opacity 0.6s;
}
.rv-carousel-enter,
.rv-carousel-leave-to {
  opacity: 0;
}

@screen lg {
  .rv-carousel--scale {
    /* padding-top: 56.25%; */
  }
}
</style>
